/**
 * Fala pelo OmniVoice — a voz do Diego (ADR-007, modo `hybrid`).
 *
 * O serviço já existe e já está no ar: POST no endpoint, texto em JSON, volta
 * `audio/ogg` binário. Não há streaming e não há sessão para manter; toda a
 * complexidade daqui é sobre cancelar na hora certa.
 */

import { DEFAULT_OMNIVOICE_VOICE, TTS_URL } from './config';
import type { Speaker } from './types';
import { comoErro } from './types';
import { relatar } from '../client/telemetry';
import { authFetch } from '../auth';
import { escolhaAtual } from './escolha';

export interface OmnivoiceOptions {
  /** Sobrescreve o endpoint. Por padrão, mesma origem (ADR-008). */
  url?: string;
  voice?: string;
  /** Ciclo de vida da fala, para o domo mudar de expressão. */
  onSpeechStart?: () => void;
  onSpeechEnd?: () => void;
  onError?: (erro: Error) => void;
}

export function createOmnivoiceSpeaker(opts: OmnivoiceOptions = {}): Speaker {
  const url = opts.url ?? TTS_URL;
  const voice = opts.voice ?? DEFAULT_OMNIVOICE_VOICE;

  /**
   * Um único elemento, reaproveitado por todas as falas. No iOS o áudio só
   * toca depois de liberado por um gesto do usuário, e a liberação vale para
   * o elemento e não para a página: criar um `new Audio()` a cada fala
   * perderia a liberação e o Will emudeceria no segundo turno.
   */
  const audio = new Audio();
  audio.preload = 'auto';

  /**
   * Libera o elemento no primeiro gesto do usuário, qualquer que seja.
   *
   * O navegador recusa `play()` programático até que a pessoa interaja com a
   * página, e a permissão é concedida ao ELEMENTO, não ao documento. Quem só
   * digitava nunca clicava em "Ouvir", então nenhum gesto chegava a este
   * elemento e toda fala era barrada — o áudio era gerado, baixado e descartado
   * silenciosamente. O código anterior detectava o bloqueio e reportava, mas
   * nunca fazia nada a respeito.
   *
   * Tocar e pausar um WAV silencioso de 44 bytes é o que concede a permissão.
   */
  const SILENCIO =
    'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=';
  let liberado = false;
  const liberar = (): void => {
    if (liberado) return;
    liberado = true;
    const anterior = audio.src;
    audio.muted = true;
    audio.src = SILENCIO;
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
      })
      .catch(() => {
        // Se nem o silêncio tocou, o gesto não valeu; a próxima interação
        // tenta de novo.
        liberado = false;
      })
      .finally(() => {
        audio.muted = false;
        if (anterior !== '') audio.src = anterior;
      });
  };
  if (typeof document !== 'undefined') {
    for (const evento of ['pointerdown', 'keydown', 'touchstart'] as const) {
      document.addEventListener(evento, liberar, { capture: true });
    }
  }

  let requisicao: AbortController | null = null;
  let urlObjeto: string | null = null;
  /** Identifica a fala corrente. Tudo que chega de uma fala antiga é descartado. */
  let turno = 0;
  let encerrarPendente: (() => void) | null = null;
  let falando = false;

  const liberarUrl = (): void => {
    if (urlObjeto) {
      URL.revokeObjectURL(urlObjeto);
      urlObjeto = null;
    }
  };

  const marcarFim = (): void => {
    if (falando) {
      falando = false;
      opts.onSpeechEnd?.();
    }
  };

  const hush = (): void => {
    // Invalidar o turno antes de qualquer coisa: se a resposta HTTP chegar
    // um instante depois do abort, ela já não terá dono e não vai tocar.
    turno++;
    requisicao?.abort();
    requisicao = null;
    audio.pause();
    audio.onended = null;
    audio.onerror = null;
    audio.removeAttribute('src');
    liberarUrl();
    marcarFim();
    // hush() não é falha: quem esperava o fim da fala segue em frente.
    const fecha = encerrarPendente;
    encerrarPendente = null;
    fecha?.();
  };

  const speak = async (text: string, estado?: string): Promise<void> => {
    const limpo = text.trim();
    if (!limpo) return;

    hush(); // uma fala por vez; a nova sempre vence a anterior
    const meuTurno = ++turno;

    const controle = new AbortController();
    requisicao = controle;

    // Decidida uma vez e reusada: pedir e relatar a partir de duas leituras
    // separadas de `escolhaAtual()` permitiria relatar uma voz e falar outra
    // se a escolha mudasse no meio — que é precisamente o defeito que este
    // relato existe para detectar.
    const vozPedida = escolhaAtual().vozId ?? voice;

    let resposta: Response;
    try {
      // `authFetch`, e não `fetch`: cada fala é uma síntese cobrada na conta de
      // provedor do titular, e o proxy passou a exigir a sessão do U1 antes de
      // deixar passar. O token é o que o navegador tem e quem varre a internet
      // não tem — o `Origin` que guardava esta rota até aqui era escolha de
      // quem escrevia o cliente. `authFetch` só anexa a credencial em mesma
      // origem, então um `opts.url` apontando para fora não a leva embora.
      resposta = await authFetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'audio/ogg' },
        /**
         * REGRA CRÍTICA DO OMNIVOICE: a geração é single-pass. O texto vai
         * inteiro, numa requisição só. Quebrar em sentenças e concatenar os
         * áudios parece ganhar latência, mas o modelo perde a prosódia da
         * frase seguinte e as emendas ficam audíveis — a voz deixa de soar
         * como o Diego, que é a única razão de o modo `hybrid` existir.
         */
        // O estado vai junto para o servidor escolher o perfil de prosódia.
        // Quem prepara o texto é ele, e não este cliente: o cache do /api/tts é
        // chaveado no texto já preparado, e preparar aqui faria o navegador e o
        // servidor discordarem sobre qual texto é o mesmo texto.
        // A voz e o modo vêm da escolha persistida no navegador, e caem para o
        // padrão do servidor quando ninguém escolheu. Antes disto, trocar a voz
        // do Will exigia editar `/etc/will-voice.env` e reiniciar o serviço —
        // comparar duas vozes era um procedimento de máquina, então na prática
        // ninguém comparava.
        body: JSON.stringify({
          text: limpo,
          // A escolha do usuário vence o padrão de compilação, e a ordem
          // importa: `voice` acima NUNCA é indefinido — ele já cai em
          // `DEFAULT_OMNIVOICE_VOICE`. Com `voice ?? escolha` a escolha jamais
          // era consultada, e o Will falava com a voz padrão enquanto a bancada
          // tocava a escolhida. Na sessão isso soava como vozes múltiplas.
          //
          // Lido a CADA fala, e não uma vez: trocar de voz na bancada precisa
          // valer no próximo turno, sem recarregar a página.
          // `voice_id`, e NÃO `voice`. O servidor lê `j.voice_id`; este cliente
          // mandava `voice`, que ele ignorava em silêncio e caía no padrão de
          // `/etc/will-voice.env`. Efeito: a bancada de vozes (que sempre mandou
          // `voice_id`) tocava a voz escolhida e a SESSÃO falava com a padrão —
          // é exatamente isso que o Márcio ouvia como "vozes múltiplas".
          //
          // Os dois lados são JavaScript e não compartilham contrato, então
          // nenhum type-check via a divergência. A conferência que pega isto
          // agora é o número: `u1_fala_voz_total` conta por voz, e duas vozes
          // na mesma sessão viram duas séries.
          voice_id: vozPedida,
          state: estado,
          mode: escolhaAtual().modo ?? undefined,
          // Lido a cada fala, como a voz: trocar o idioma na bancada precisa
          // valer no próximo turno, sem recarregar a página.
          language: escolhaAtual().idioma ?? undefined,
        }),
        signal: controle.signal,
      });
    } catch (e) {
      if (controle.signal.aborted) return; // interrompido durante a geração
      const erro = comoErro(e);
      opts.onError?.(erro);
      throw erro;
    }

    if (meuTurno !== turno) return;

    if (!resposta.ok) {
      const erro = new Error(
        `OmniVoice respondeu ${resposta.status}. A fala deste turno não saiu.`,
      );
      opts.onError?.(erro);
      throw erro;
    }

    // A voz que SAIU, relatada por fala. O servidor devolve `X-Will-Voice` com
    // o que ele de fato usou — que pode não ser o pedido, se o id não existir
    // no catálogo dele. Relatar o pedido em vez do usado esconderia justamente
    // o caso interessante.
    relatar('voz.falou', {
      detalhe: resposta.headers.get('X-Will-Voice') ?? vozPedida ?? 'padrao-do-servidor',
    });

    const blob = await resposta.blob();
    if (meuTurno !== turno) return;

    requisicao = null;
    liberarUrl();
    urlObjeto = URL.createObjectURL(blob);
    audio.src = urlObjeto;

    falando = true;
    opts.onSpeechStart?.();

    return new Promise<void>((resolve) => {
      const terminar = (): void => {
        if (meuTurno !== turno) return; // já substituído por outra fala
        audio.onended = null;
        audio.onerror = null;
        liberarUrl();
        marcarFim();
        encerrarPendente = null;
        resolve();
      };

      encerrarPendente = resolve;
      audio.onended = terminar;
      audio.onerror = (): void => {
        opts.onError?.(new Error('O navegador não conseguiu tocar o áudio do OmniVoice.'));
        terminar();
      };

      audio.play().catch((e: unknown) => {
        // Bloqueio de autoplay é o caso comum aqui. Reportamos com uma
        // mensagem que diz o que fazer, em vez de repassar o erro do
        // navegador — e seguimos, porque travar a conversa por causa do áudio
        // seria pior que ficar mudo neste turno.
        const erro = comoErro(e);
        const bloqueio = erro.name === 'NotAllowedError' || /gesture|interact|not allowed/i.test(erro.message);
        relatar(bloqueio ? 'voz.autoplay.bloqueado' : 'voz.tts.falhou', { detalhe: erro.message });
        console.warn('[u1] falha ao tocar o áudio:', erro.name, erro.message);
        opts.onError?.(
          bloqueio
            ? new Error('O navegador bloqueou o áudio. Clique em qualquer lugar da página e fale de novo.')
            : erro,
        );
        terminar();
      });
    });
  };

  return { speak, hush };
}

/** Modo `text` (ADR-007): a resposta aparece escrita, nada é falado. */
export function createSilentSpeaker(): Speaker {
  return {
    speak: async (): Promise<void> => undefined,
    hush: (): void => undefined,
  };
}
